import type { GridFilterItem } from "@mui/x-data-grid-pro";

import type { CellValue, IGridFilterConfig, GridRow } from "@/apps/engagement-letter-database/data/types";

import { stringify } from "./stringify";

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// Parses a grid date-filter value (a "YYYY-MM-DD" string or a Date) into a
// local start-of-day timestamp so comparisons are timezone-safe.
function parseFilterDate(value: unknown): number | null {
  if (value instanceof Date) return startOfDay(value);
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
  }
  return null;
}

function matchesDateFilter(cell: CellValue, operator: string, value: unknown): boolean {
  if (!(cell instanceof Date)) return false;
  const cellTime = startOfDay(cell);
  const filterTime = parseFilterDate(value);
  if (filterTime === null) return true;
  switch (operator) {
    case 'not':
      return cellTime !== filterTime;
    case 'after':
      return cellTime > filterTime;
    case 'onOrAfter':
      return cellTime >= filterTime;
    case 'before':
      return cellTime < filterTime;
    case 'onOrBefore':
      return cellTime <= filterTime;
    default:
      return cellTime === filterTime;
  }
}

function matchesStringFilter(cell: CellValue, operator: string, value: unknown): boolean {
  const str = stringify(cell).toLowerCase();
  const val = typeof value === 'string' || typeof value === 'number' ? String(value).toLowerCase() : '';
  switch (operator) {
    case 'doesNotContain':
      return !str.includes(val);
    case 'equals':
      return str === val;
    case 'doesNotEqual':
      return str !== val;
    case 'startsWith':
      return str.startsWith(val);
    case 'endsWith':
      return str.endsWith(val);
    case 'isAnyOf':
      return Array.isArray(value) && value.map((v) => String(v).toLowerCase()).includes(str);
    default:
      return str.includes(val);
  }
}

export function matchesFilterItem(row: GridRow, item: GridFilterItem, config: IGridFilterConfig): boolean {
  const { field, operator, value } = item;
  const cell = field in row ? row[field] : null;

  // Operators that don't require a value.
  if (operator === 'isEmpty') return cell == null || cell === '';
  if (operator === 'isNotEmpty') return cell != null && cell !== '';

  // Empty value => grid treats the filter as inactive.
  const emptyValue = value === undefined || value === null || value === '';
  if (emptyValue && !(operator === 'isAnyOf' && Array.isArray(value) && value.length)) return true;

  if (config.dateFields.includes(field)) return matchesDateFilter(cell, operator, value);
  if (config.booleanFields.includes(field)) return Boolean(cell) === (value === true || value === 'true');
  return matchesStringFilter(cell, operator, value);
}